import type { Page } from '@/payload-types'
import { slugifyHeading } from '@/utilities/slugifyHeading'

/** URL segment of the article section. Change here only. */
export const ARTICLES_BASE = 'blog'

export const articlePath = (slug: string): string => `/${ARTICLES_BASE}/${slug}`

const WORDS_PER_MINUTE = 200

type LexicalNode = {
  type?: string
  tag?: string
  text?: string
  children?: LexicalNode[]
}

/** Every richText root found in the page layout (Content block columns). */
export function layoutRichTexts(layout: Page['layout']): LexicalNode[] {
  const roots: LexicalNode[] = []
  for (const block of layout || []) {
    const columns = (block as { columns?: { richText?: { root?: LexicalNode } }[] }).columns
    for (const col of columns || []) {
      if (col.richText?.root) roots.push(col.richText.root)
    }
  }
  return roots
}

export function nodeText(node: LexicalNode): string {
  if (typeof node.text === 'string') return node.text
  return (node.children || []).map(nodeText).join('')
}

export function readingTimeMinutes(layout: Page['layout']): number {
  const words = layoutRichTexts(layout)
    .map(nodeText)
    .join(' ')
    .split(/\s+/)
    .filter(Boolean).length
  return Math.max(1, Math.ceil(words / WORDS_PER_MINUTE))
}

export type TocItem = { id: string; text: string; level: 2 | 3 }

export function extractToc(layout: Page['layout']): TocItem[] {
  const items: TocItem[] = []
  const walk = (node: LexicalNode) => {
    if (node.type === 'heading' && (node.tag === 'h2' || node.tag === 'h3')) {
      const text = nodeText(node).trim()
      if (text) items.push({ id: slugifyHeading(text), text, level: node.tag === 'h2' ? 2 : 3 })
      return
    }
    node.children?.forEach(walk)
  }
  layoutRichTexts(layout).forEach(walk)
  return items
}
